import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { UserClaim } from '../models/cookie-auth/user-claim';
import { ConfigService } from './config.service';
import { CsrfService } from './csrf.service';

const CSRF_FORM_FIELD_NAME = '__RequestVerificationToken';

@Injectable({
  providedIn: 'root',
})

export class AuthService {
  private baseUrl = '';
  public userId = '';
  public clientNameSpace = '';
  private userClaims: UserClaim[] = [];
  public $userClaims: BehaviorSubject<UserClaim[]> =
    new BehaviorSubject<UserClaim[]>([]);

  // Promise that resolves once config with servicesUrl has been loaded
  private configReadyPromise: Promise<any>;

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private csrfService: CsrfService
  ) {
    // Create a one-time promise that resolves when config is "real"
    this.configReadyPromise = this.configService.$configuration
      .pipe(
        // Wait until we actually have a config with servicesUrl
        filter(cfg => !!cfg && !!cfg.servicesUrl),
        take(1)
      )
      .toPromise()
      .then(cfg => {
        if (!cfg?.servicesUrl) {
          throw new Error('Missing servicesUrl configuration');
        }

        this.baseUrl = cfg.servicesUrl;
        return cfg;
      });
  }

  private async ensureConfigReady(): Promise<void> {
    // Just await the one-time promise; no extra subscriptions scattered around
    await this.configReadyPromise;
  }

  private getSafeReturnUrl(): string {
    return window.location.origin + '/';
  }

  private requireBaseUrl(): string {
    if (!this.baseUrl) {
      throw new Error('Missing servicesUrl configuration');
    }

    return this.baseUrl;
  }

  private submitAuthForm(actionPath: string, returnUrl: string, csrfToken: string): void {
    const baseUrl = this.requireBaseUrl();
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${baseUrl}${actionPath}`;
    form.style.display = 'none';

    this.appendHiddenInput(form, 'returnUrl', returnUrl);
    this.appendHiddenInput(form, CSRF_FORM_FIELD_NAME, csrfToken);

    document.body.appendChild(form);
    form.submit();
  }

  public async signIn() {
    await this.ensureConfigReady();
    const token = await this.csrfService.ensureToken();
    if (!token) {
      throw new Error('Missing CSRF token');
    }

    this.submitAuthForm('/api/auth/signin', this.getSafeReturnUrl(), token);
  }

  public async signOut() {
    await this.ensureConfigReady();
    const token = await this.csrfService.ensureToken();
    if (!token) {
      throw new Error('Missing CSRF token');
    }

    this.submitAuthForm('/api/auth/signout', this.getSafeReturnUrl(), token);
  }

  private appendHiddenInput(form: HTMLFormElement, name: string, value: string): void {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  public async loadUserData(force = false): Promise<boolean> {
    // Make sure config is fully loaded before we touch the user endpoint
    await this.ensureConfigReady();

    if (!force && this.userClaims && this.userClaims.length > 0) {
      return true;
    }

    try {
      const baseUrl = this.requireBaseUrl();
      const claims = await this.http
        .get<UserClaim[]>(`${baseUrl}/api/auth/user`)
        .toPromise();

      this.userClaims = claims || [];
      this.$userClaims.next(this.userClaims);
      await this.csrfService.refreshToken();
      return this.isSignedIn();
    } catch (_) {
      this.userClaims = [];
      this.$userClaims.next(this.userClaims);
      this.csrfService.clearToken();
      return false;
    }
  }

  public isSignedIn(): boolean {
    return !!this.userClaims && this.userClaims.length > 0;
  }
}